import { UnifiedSearchIcon } from '@components/Icons/UnifiedSearch';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { availableSearchTypeOptions } from '@lib/utils/searchTypeOptions';
import { showToast } from '@lib/utils/toastmaster';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import TranscriptIcon from '@mui/icons-material/RecordVoiceOver';
import EyeIcon from '@mui/icons-material/RemoveRedEye';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { SvgIcon, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { SegmentType } from '@stores/Library/LibrarySearch';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { LibraryManagerMultiLibrarySearch } from '@stores/LibraryManager/LibraryManagerMultiLibrarySearch';
import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import { ResultTypeDrawer } from './components/ResultTypeDrawer/ResultTypeDrawer';
import {
  ConversationalSearchTooltip,
  EXPERIMENTAL_BADGE,
  TooltipBadge,
  UnifiedSearchTooltip,
  VisualSearchTooltip,
} from './components/ResultTypeTooltip/ResultTypeTooltip';
import css from './ResultTypeSwitch.module.scss';

export const ResultTypeSwitch = observer(() => {
  const isMobile = useIsMobile();
  const manager = useLibraryManager();
  const search = manager.multiLibSearch;
  const groupRef = useRef<HTMLDivElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const setResultType = (type: SegmentType) => {
    if (type === search.resultType) return;
    let descriptionType: string = type;
    if (type === 'transcript') descriptionType = 'conversational';
    if (type === 'unified') descriptionType = 'multimodal';

    search.setResultType(type);
    showToast('search-mode-updated', { descriptionType });
  };

  // Unified search is pulled from the FE (the "Enable unified search" debug
  // flag revives it). Derived from availableSearchTypeOptions — the single
  // source of selectable modes — so this surface can't drift from the mobile
  // drawer or the URL parser if the helper ever gains another gate.
  //
  // No reset effect here: falling out of unified on a flag flip is owned by
  // SearchParamsInURL's mode effect (always co-mounted on the search page,
  // keyed on the same flag), which resets silently — a toast from this
  // component's setResultType wrapper would announce a repair the user never
  // initiated.
  const hasUnifiedFlag = availableSearchTypeOptions().some((o) => o.type === 'unified');

  useEffect(() => {
    if (groupRef.current) {
      setAnchorEl(groupRef.current);
    }
  }, []);

  const handleMobileClick = () => {
    setDrawerOpen(true);
  };

  // Visual + Unified modes are gated on library scope: disabled when
  // every selected library is transcript-only (podcast-only scope),
  // warned when some libraries are transcript-only (mixed scope).
  const visualsDisabled = search.allScopeIsTranscriptOnly;
  const visualBadge = scopeAwareBadge('visual', search);
  const unifiedBadge = scopeAwareBadge('unified', search);

  const visualButton = (
    <ToggleButton value="visual" disabled={visualsDisabled}>
      <EyeIcon className={css.resultTypeIcon} />
    </ToggleButton>
  );
  const unifiedButton = (
    <ToggleButton value="unified" disabled={visualsDisabled}>
      <SvgIcon sx={{ width: 16, height: 16 }}>
        <UnifiedSearchIcon />
      </SvgIcon>
    </ToggleButton>
  );

  return (
    <>
      <div className={css.toggleGroup} onClick={isMobile ? handleMobileClick : undefined}>
        <ToggleButtonGroup
          ref={groupRef}
          color="primary"
          exclusive
          value={search.resultType}
          onChange={(_, value) => {
            if (!isMobile && value) setResultType(value);
          }}
        >
          <VisualSearchTooltip anchorEl={anchorEl} badge={visualBadge}>
            {/*
              MUI Tooltip injects event handlers onto its direct child via
              cloneElement. A disabled <ToggleButton> ignores pointer
              events, so wrap it in a <span> to receive hover — but the
              span must be the tooltip's direct child, not a component
              indirection, or cloneElement hits the indirection and the
              handlers never reach the DOM.
            */}
            {visualsDisabled ? (
              <span className={css.disabledWrap}>{visualButton}</span>
            ) : (
              visualButton
            )}
          </VisualSearchTooltip>
          <ConversationalSearchTooltip anchorEl={anchorEl}>
            <ToggleButton value="transcript">
              <TranscriptIcon className={css.resultTypeIcon} />
            </ToggleButton>
          </ConversationalSearchTooltip>
          {hasUnifiedFlag && (
            <UnifiedSearchTooltip anchorEl={anchorEl} badge={unifiedBadge}>
              {visualsDisabled ? (
                <span className={css.disabledWrap}>{unifiedButton}</span>
              ) : (
                unifiedButton
              )}
            </UnifiedSearchTooltip>
          )}
        </ToggleButtonGroup>
      </div>
      {isMobile && (
        <ResultTypeDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onOpen={() => setDrawerOpen(true)}
          selectedType={search.resultType}
          onSelect={setResultType}
          scopeBadge={scopeBadgeFor(search)}
          disableVisualAndUnified={search.allScopeIsTranscriptOnly}
        />
      )}
    </>
  );
});

/**
 * Scope-only badge — error when the entire search scope is
 * transcript-only, warning when some but not all are. Returns null
 * when no transcript-only libraries are in scope. Mode-agnostic, so
 * the mobile drawer can use it directly (drawer shows all modes at
 * once, so "this mode is experimental" isn't the right framing).
 */
function scopeBadgeFor(search: LibraryManagerMultiLibrarySearch): TooltipBadge | null {
  const label = search.transcriptOnlySentence;
  if (!label) return null;
  if (search.allScopeIsTranscriptOnly) {
    return { variant: 'error', label, icon: <CancelRoundedIcon /> };
  }
  return { variant: 'warning', label, icon: <WarningAmberRoundedIcon /> };
}

/**
 * Mode-specific tooltip badge for the desktop toggle-button tooltips.
 * Reuses the scope badge when relevant; falls back to the legacy
 * Experimental ribbon on the Unified tooltip otherwise.
 */
function scopeAwareBadge(
  mode: 'visual' | 'unified',
  search: LibraryManagerMultiLibrarySearch,
): TooltipBadge | null {
  return scopeBadgeFor(search) ?? (mode === 'unified' ? EXPERIMENTAL_BADGE : null);
}
